import { buttonVariants } from "@patient-watch/ui/button";
import { cn } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { usePublicFormSignup } from "@/features/forms/hooks/usePublicFormSignup";
import { SingleReview } from "@/components/SingleReview";
import { trackEvent } from "@/utils/trackEvent";
import type { EntryValueViewT } from "@patient-watch/database";

const RESULTS_STORAGE_KEY = (formId: string, entryId: string) => `pw_public_form_results:${formId}:${entryId}`;
const CONVERSION_CTA = "Sign up free";

const CompleteReviews = () => {
    const { formId, diaryId, trackerId, entryId } = Route.useParams();
    const navigate = useNavigate();
    const [stored, setStored] = useState<{
        entryValues: EntryValueViewT[];
        diaryId: string;
        formId: string;
    } | null>(null);

    const { handleStartTracking } = usePublicFormSignup(formId, stored?.diaryId);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(RESULTS_STORAGE_KEY(formId, entryId));
            if (!raw) {
                navigate({
                    to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/results",
                    params: { formId, diaryId, trackerId, entryId },
                });
                return;
            }
            const parsed = JSON.parse(raw) as { entryValues: EntryValueViewT[]; diaryId: string; formId: string };
            setStored(parsed);
            trackEvent("reviews_view", { form_id: formId, diary_id: parsed.diaryId });
        } catch {
            navigate({
                to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/results",
                params: { formId, diaryId, trackerId, entryId },
            });
        }
    }, [formId, diaryId, trackerId, entryId, navigate]);

    if (!stored) return null;

    return (
        <section className="box-border flex flex-col gap-y-8 mx-auto w-full min-w-0 max-w-6xl px-2 py-8">
            <Link
                to="/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/results"
                params={{ formId, diaryId, trackerId, entryId }}
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            >
                <ChevronLeft size={16} />
                Back to results
            </Link>

            <div className="flex flex-col gap-8">
                <div className="text-center">
                    <h1 className="font-bold text-2xl text-slate-900">What clinicians say</h1>
                    <p className="mt-2 text-slate-600 text-sm">
                        Join leading MSK clinicians gathering their patient outcomes
                    </p>
                </div>

                <SingleReview />

                <div className="flex flex-col items-center gap-4">
                    <button
                        onClick={handleStartTracking}
                        className={cn(
                            buttonVariants({ size: "lg" }),
                            "flex justify-center items-center gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg rounded-xl px-8 font-semibold text-white transition-colors"
                        )}
                    >
                        <Sparkles className="w-5 h-5" />
                        {CONVERSION_CTA}
                    </button>
                </div>
            </div>
        </section>
    );
};

export const Route = createFileRoute(
    "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/reviews"
)({
    component: CompleteReviews,
});
